<script lang="ts" setup>
import { SshKey as SshKeySchema, type SshKey, type SshKeyUpdateRequest } from '@/types'
import { STATUS_CODES } from '@/constants'
import { defineAsyncComponent, onMounted, reactive, ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'

const SshKeyCreateModal = defineAsyncComponent(() => import('./sections/SshKeyCreateModal.vue'))
const SshKeyDeleteModal = defineAsyncComponent(() => import('./sections/SshKeyDeleteModal.vue'))

const { t } = useI18n()
const notifyStore = useNotifyStore()

const data = reactive({
  keys: [] as SshKey[],
  loading: false
})

const showCreateModal = ref(false)
const showDeleteModal = ref(false)
const keyToDelete = ref<SshKey | null>(null)

const formatDate = (dateString: string): string => {
  const dt = DateTime.fromISO(dateString)
  return dt.toLocaleString(DateTime.DATE_MED, { locale: 'de' })
}

const fetchKeys = () => {
  data.loading = true
  useAxios<null, SshKey[]>('GET', '/account/ssh-keys', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = SshKeySchema.array().safeParse(res.data)
        if (!validate.success) return
        data.keys = Array.from(validate.data)
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'SshKeys' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('errors.loadFailed'),
        message: getErrorMessage(error)
      })
    })
    .finally(() => {
      data.loading = false
    })
}

const openCreateModal = () => {
  showCreateModal.value = true
}

const handleKeyCreated = (key: SshKey) => {
  showCreateModal.value = false
  data.keys = [...data.keys, key]
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'success',
    title: t('dashboard.account.sshKeys.notifications.createSuccess'),
    message: key.name
  })
}

const openDeleteModal = (key: SshKey) => {
  keyToDelete.value = key
  showDeleteModal.value = true
}

const handleKeyDeleted = (key: SshKey) => {
  data.keys = data.keys.filter((k) => k.id !== key.id)
  showDeleteModal.value = false
  keyToDelete.value = null
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'success',
    title: t('dashboard.account.sshKeys.notifications.deleteSuccess'),
    message: key.name
  })
}

const toggleKeyActive = async (key: SshKey) => {
  const newActive = !key.active
  try {
    const res = await useAxios<SshKeyUpdateRequest, SshKey>(
      'PATCH',
      `/account/ssh-keys/${key.id}`,
      true,
      { active: newActive }
    )
    if (res && res.status === STATUS_CODES.OK) {
      const validate = SshKeySchema.safeParse(res.data)
      if (validate.success) {
        data.keys = data.keys.map((k) => (k.id === key.id ? validate.data : k))
      }
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'SshKeys' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error)
    })
  }
}

onMounted(() => {
  fetchKeys()
})
</script>

<template>
  <div class="ssh-keys-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon icon="mdi-key-chain-variant" size="20" />
        </div>
        <div class="header-text">
          <h2 class="page-title">{{ $t('dashboard.account.sshKeys.title') }}</h2>
          <p class="page-subtitle">{{ $t('dashboard.account.sshKeys.subtitle') }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="action-btn primary" @click="openCreateModal">
          <v-icon icon="mdi-plus" size="18" />
          <span>{{ $t('dashboard.account.sshKeys.createKey') }}</span>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="page-content">
      <!-- Loading State -->
      <div v-if="data.loading" class="loading-state">
        <v-progress-circular indeterminate size="32" width="2" color="primary" />
        <span>{{ $t('common.loading') }}</span>
      </div>

      <!-- Keys Table -->
      <div v-else-if="data.keys.length > 0" class="keys-table-wrapper">
        <table class="keys-table">
          <thead>
            <tr>
              <th class="col-name">{{ $t('dashboard.account.sshKeys.columns.name') }}</th>
              <th class="col-fingerprint">{{ $t('dashboard.account.sshKeys.columns.fingerprint') }}</th>
              <th class="col-created">{{ $t('dashboard.account.sshKeys.columns.created') }}</th>
              <th class="col-status">{{ $t('dashboard.account.sshKeys.columns.status') }}</th>
              <th class="col-actions">{{ $t('dashboard.account.sshKeys.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in data.keys" :key="key.id" class="key-row">
              <td class="col-name">
                <code>{{ key.name }}</code>
              </td>
              <td class="col-fingerprint">
                <span class="fingerprint-text">{{ key.fingerprint }}</span>
              </td>
              <td class="col-created">
                <span class="date-text">{{ formatDate(key.created) }}</span>
              </td>
              <td class="col-status">
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="key.active ? 'success' : 'grey'"
                  class="status-chip"
                  @click="toggleKeyActive(key)"
                >
                  {{ key.active ? $t('dashboard.account.sshKeys.active') : $t('dashboard.account.sshKeys.inactive') }}
                </v-chip>
              </td>
              <td class="col-actions">
                <button
                  class="icon-btn danger"
                  :title="$t('common.delete')"
                  @click="openDeleteModal(key)"
                >
                  <v-icon icon="mdi-trash-can" size="18" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <v-icon icon="mdi-key-chain-variant" size="40" />
        </div>
        <h3 class="empty-title">{{ $t('dashboard.account.sshKeys.empty.title') }}</h3>
        <p class="empty-text">{{ $t('dashboard.account.sshKeys.empty.description') }}</p>
        <button class="action-btn primary" @click="openCreateModal">
          <v-icon icon="mdi-plus" size="18" />
          <span>{{ $t('dashboard.account.sshKeys.createKey') }}</span>
        </button>
      </div>
    </div>

    <!-- Create Modal -->
    <SshKeyCreateModal
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="handleKeyCreated"
    />

    <!-- Delete Modal -->
    <SshKeyDeleteModal
      v-if="keyToDelete"
      :show="showDeleteModal"
      :ssh-key="keyToDelete"
      @close="showDeleteModal = false"
      @deleted="handleKeyDeleted"
    />
  </div>
</template>

<style lang="scss" scoped>
.ssh-keys-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08));
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 10px;
  color: #64b5f6;
}

.page-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.page-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.primary {
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.25);
    color: #64b5f6;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.25);
      border-color: rgba(33, 150, 243, 0.4);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-content {
  min-height: 200px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  color: rgba(200, 218, 255, 0.6);
}

.keys-table-wrapper {
  overflow-x: auto;
}

.keys-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 12px 14px;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(15, 23, 48, 0.3);
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  }

  .col-name { width: 24%; }
  .col-fingerprint { width: 33%; }
  .col-created { width: 18%; }
  .col-status { width: 13%; }
  .col-actions { width: 12%; text-align: right; }
}

.key-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  td {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }
}

.status-chip {
  cursor: pointer;
}

code {
  background: rgba(120, 163, 255, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: rgba(200, 218, 255, 0.9);
}

.fingerprint-text {
  font-size: 0.82rem;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: rgba(200, 218, 255, 0.7);
  word-break: break-all;
}

.date-text {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }

  &.danger:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 24px;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(120, 163, 255, 0.08);
  border-radius: 18px;
  color: rgba(200, 218, 255, 0.3);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px;
}

.empty-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0 0 20px;
  max-width: 320px;
}

/* Light Theme */
.v-theme--lightTheme {
  .page-title, .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .page-subtitle, .empty-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .keys-table th {
    color: rgba(15, 23, 42, 0.6);
    background: rgba(15, 23, 42, 0.03);
  }

  .key-row td {
    color: rgba(15, 23, 42, 0.85);
  }

  code {
    background: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.9);
  }

  .fingerprint-text {
    color: rgba(15, 23, 42, 0.7);
  }

  .date-text {
    color: rgba(15, 23, 42, 0.7);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .keys-table {
    .col-fingerprint, .col-created {
      display: none;
    }
  }
}
</style>
